import React from "react";

import Image from "../../components/image";
import { useTheme } from "../../context/ThemeContext";
import URLogo from "../../images/ur-logo-white.inline.svg";
import BWLogo from "images/bw-light-logo.inline.svg";
import {
  formatCurrencyAmount,
  shortcodePreProcessor
} from "../../utils/formatContent";
import Stars from "./stars";

const CourseInformation = ({
  genDetails,
  testimonials,
  programCode,
  coursePrice,
  isBushwiseProgram
}) => {
  const { currentSiteMeta } = useTheme();

  // console.log("currentSiteMeta", currentSiteMeta);

  const programCost =
    formatCurrencyAmount({
      currentSiteMeta,
      amount: coursePrice?.price
    }) ?? "";

  const infoData = [
    {
      title: "Next start date",
      data:
        coursePrice?.instanceFormattedDate ??
        coursePrice?.instanceDate ??
        genDetails.enrollmentNextStartDate
    },
    {
      title: "Enrollment closes",
      data: genDetails.enrollmentClosingDate
    },
    {
      title: "Course frequency",
      data: genDetails.enrollmentCourseFrequency
    },
    {
      title: "Duration",
      data: coursePrice?.duration ?? genDetails.enrollmentCourseDuration
    },
    {
      title: "Time committment",
      data: coursePrice?.timeHours ?? "- hours"
    },
    {
      title: "Format",
      data: "Online"
    },
    {
      title: "Optional extra",
      data: genDetails?.paymentDetailsTitle
    }
  ];

  const currencySymbol = currentSiteMeta?.currency_symbol;
  const onlineImage = genDetails.enrollmentImage?.localfile;
  const fallbackImg = genDetails.enrollmentImage?.sourceUrl;

  const ratings = testimonials
    ?.filter((filterRating) => filterRating?.rating > 1)
    ?.map((rating) => {
      return {
        totals: rating.rating
      };
    });

  const totals = ratings?.map((total) => {
    return (total = total.totals);
  });

  function getAvg(totals) {
    const total = totals?.reduce((acc, c) => acc + c, 0);
    return total / totals?.length;
  }

  const avgRating = getAvg(totals).toFixed(1);

  const { paymentDetailsDescription } = genDetails || {};
  const programFees = genDetails?.onlineScholarshipDiscounts ?? [];

  const paymentDetails = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: paymentDetailsDescription
  });

  return (
    <div className="z-20 flex flex-col md:flex-row justify-between -mt-16 mx-auto w-11/12 md:w-5/6 bg-yellow-standard planet:bg-gray-darkest rounded-xs">
      <Image
        image={onlineImage}
        imageAlt={"Course Information"}
        sourceUrl={fallbackImg}
        imageClass={
          "object-cover object-center rounded-xs shrink-0 m-4 md:m-6 h-[82vw] md:h-auto"
        }
      />
      <div className="divide-y divide-white planet:divide-[#3C3C3C] w-full text-white p-4 md:p-16">
        <div className="flex flex-col md:flex-row justify-between mb-6 pt-6 md:pt-0 border-t border-white planet:border-[#3C3C3C] md:border-0">
          <h3
            className="text-white font-normal planet:font-semibold mb-3 md:mb-0"
            dangerouslySetInnerHTML={{ __html: "Course information" }}
          />
          {Number.isInteger(parseInt(avgRating)) && (
            <div className="">
              <div className="flex items-center gap-1">
                <div
                  className="text-mob text-base planet:text-gray-generic pr-1"
                  dangerouslySetInnerHTML={{ __html: "Student rating" }}
                />
                <div
                  className="h3 !text-white planet:font-bold"
                  dangerouslySetInnerHTML={{ __html: `${avgRating * 2}/10` }}
                />
              </div>
              <div className="flex justify-start md:justify-end">
                <Stars
                  stars={(avgRating / 10) * 10}
                  starColorClass={{
                    fill: "text-white planet:text-gray-generic",
                    back: "text-yellow-standard planet:text-transparent",
                    stroke: "stroke-white planet:stroke-gray-generic"
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="md:grid grid-flow-col md:grid-cols-3 md:grid-rows-3 pt-6 md:pt-11">
          {infoData.map((info, index) => (
            <div className="mb-3 md:mb-0" key={index}>
              <div
                className="text-yellow-light planet:text-gray-generic"
                dangerouslySetInnerHTML={{ __html: info.title }}
              />
              <div
                className="mt-1 font-normal md:font-semibold planet:font-normal"
                dangerouslySetInnerHTML={{ __html: info.data }}
              />
            </div>
          ))}

          <div className="md:row-span-2">
            <div className="flex gap-2">
              <div className="flex flex-col justify-center basis-1/2 rounded-xs bg-yellow-light planet:bg-gray-darker px-5 py-3">
                <div
                  className="planet:text-gray-generic"
                  dangerouslySetInnerHTML={{ __html: "Price*" }}
                />

                {programCost && (
                  <div
                    className="font-alice text-xl planet:font-body planet:font-bold"
                    dangerouslySetInnerHTML={{
                      __html: programCost
                    }}
                  />
                )}
              </div>
              {!isBushwiseProgram && (
                <div className="basis-1/2 rounded-xs bg-yellow-light planet:bg-gray-darker px-5 py-3 flex items-center">
                  <div
                    className="text-xs"
                    dangerouslySetInnerHTML={{
                      __html: paymentDetails
                    }}
                  />
                </div>
              )}
            </div>
            <div
              className="mt-4 text-xs pb-4 md:pb-0 border-b md:border-b-0 planet:border-gray-generic planet:text-gray-generic"
              dangerouslySetInnerHTML={{
                __html: "*Flexible payment options available"
              }}
            />
          </div>
          <div className="py-5 md:hidden">
            <div
              className="text-xs mb-2"
              dangerouslySetInnerHTML={{
                __html: "This course was created in partnership with"
              }}
            />

            {isBushwiseProgram ? (
              <BWLogo className="w-auto md:w-full h-14" />
            ) : (
              <URLogo className="w-full h-full" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseInformation;
